import { Experiment as defaultExperiment } from '@amplitude/experiment-js-client';
import { isUnsubstitutedEnvToken as defaultIsUnsubstitutedEnvToken, isAmplitudeEnabled } from './amplitude';

const isAmplitudeExperimentsEnabled = (environment) => isAmplitudeEnabled(environment);

/**
 * @param {{
 *   Experiment?: { initializeWithAmplitudeAnalytics: (key: string) => import('@amplitude/experiment-js-client').ExperimentClient },
 *   isUnsubstitutedEnvToken?: (value: unknown) => boolean,
 * }} deps
 */
export const createAmplitudeExperiment = ({
  Experiment = defaultExperiment,
  isUnsubstitutedEnvToken = defaultIsUnsubstitutedEnvToken,
} = {}) => {
  /**
   * Central registry: experiment id → Amplitude flag key + local fallback state.
   * Use `fallbackVariant` when the experiment is driven only by the assigned variant name.
   * Keep Amplitude remote payloads the same shape as `fallback` for payload-driven flags.
   */
  const EXPERIMENTS = {
    signInButtonColor: {
      flagKey: "web-home-sign-in-join-rewards-cta-color",
      fallback: {},
      fallbackVariant: "yellow"
    },
    fullscreenHomepage: {
      flagKey: "full-screen-homepage-web",
      fallback: {},
      fallbackVariant: "control"
    },
    enterAddressUi: {
      flagKey: "enter-address-ui-web",
      fallback: {},
      fallbackVariant: "transparent-nav"
    },
    viewMenuCta: {
      flagKey: "view-menu-cta-web",
      fallback: {},
      fallbackVariant: "control"
    }
  };

  /** @type {import('@amplitude/experiment-js-client').ExperimentClient | null} */
  let experimentClient = null;

  /** Per-experiment resolved payload (first successful read caches until fetch clears). */
  const payloadCache = new Map();
  /** Per-experiment assigned variant object (first successful read caches until fetch clears). */
  const variantCache = new Map();
  /** Initial-load session override seeded by the app. */
  let experimentOverrideDisabled = false;
  /** True only after `experimentClient.fetch()` has resolved successfully. Gates `.variant()` reads so the SDK does not fire `$exposure` against an unresolved (fallback) assignment. */
  let hasFetched = false;

  const listeners = new Set();
  const EVENT_BUFFER_LIMIT = 200;
  const eventBuffer = [];
  const emit = (event) => {
    const payload = { ...event, at: Date.now() };
    eventBuffer.push(payload);
    if (eventBuffer.length > EVENT_BUFFER_LIMIT) {
      eventBuffer.shift();
    }
    listeners.forEach((listener) => {
      try {
        listener(payload);
      } catch {
        /* listener errors must never leak into experiment reads */
      }
    });
  };

  const subscribeToExperimentEvents = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const getExperimentEventHistory = () => eventBuffer.slice();

  const getExperimentConfig = (experimentId) => {
    const config = EXPERIMENTS[experimentId];
    if (!config) {
      throw new Error(`Unknown experiment: ${experimentId}`);
    }
    return config;
  };

  const getExperimentIds = () => Object.keys(EXPERIMENTS);

  const getExperimentFallback = (experimentId) => getExperimentConfig(experimentId).fallback;

  const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);

  const setExperimentOverrideDisabled = (value) => {
    const next = Boolean(value);
    if (next === experimentOverrideDisabled) {
      return;
    }
    experimentOverrideDisabled = next;
    emit({ type: 'override', disabled: next });
  };

  const isExperimentOverrideDisabled = () => experimentOverrideDisabled;

  const getFallbackVariant = (config) => {
    if (config.fallbackVariant != null) {
      return config.fallbackVariant;
    }

    if (typeof config.resolveVariant === 'function') {
      return config.resolveVariant({ payload: config.fallback }, undefined);
    }

    return 'control';
  };

  const getAssignedVariant = (experimentId) => {
    const config = getExperimentConfig(experimentId);
    if (!experimentClient || isExperimentOverrideDisabled() || !hasFetched) {
      return null;
    }
    if (variantCache.has(experimentId)) {
      return variantCache.get(experimentId);
    }

    const variant = experimentClient.variant(config.flagKey) || null;
    variantCache.set(experimentId, variant);
    emit({
      type: 'expose',
      experimentId,
      flagKey: config.flagKey,
      variant: variant?.key ?? null,
      payload: isPlainObject(variant?.payload) ? variant.payload : null,
    });
    return variant;
  };

  /**
   * Reads an experiment's payload. Returns fallback if the client isn't ready,
   * no variant was assigned, or the payload is missing/wrong-shaped.
   * Fires Amplitude's automatic $exposure on first call per flag per session.
   *
   * @param {string} experimentId - must exist on EXPERIMENTS
   */
  const getExperimentPayload = (experimentId) => {
    const config = getExperimentConfig(experimentId);
    if (!experimentClient || isExperimentOverrideDisabled() || !hasFetched) {
      return config.fallback;
    }
    if (payloadCache.has(experimentId)) {
      return payloadCache.get(experimentId);
    }

    const variant = getAssignedVariant(experimentId);
    const payload = isPlainObject(variant?.payload) ? variant.payload : config.fallback;

    payloadCache.set(experimentId, payload);
    return payload;
  };

  const getExperimentVariant = (experimentId) => {
    const config = getExperimentConfig(experimentId);
    const variant = getAssignedVariant(experimentId);
    const fallbackVariant = getFallbackVariant(config);

    if (typeof config.resolveVariant === 'function') {
      return config.resolveVariant(
        {
          ...variant,
          payload: isPlainObject(variant?.payload) ? variant.payload : config.fallback,
        },
        fallbackVariant
      );
    }

    return variant?.key || fallbackVariant;
  };

  /**
   * Call after Amplitude Analytics `init`. Uses deployment key from the same Amplitude project.
   * @param {object} environment - window.environment
   */
  const initializeAmplitudeExperiment = (environment) => {
    if (
      typeof window === 'undefined' ||
      experimentClient ||
      isExperimentOverrideDisabled()
    ) {
      return;
    }
    if (!isAmplitudeExperimentsEnabled(environment)) {
      setExperimentOverrideDisabled(true);
      return;
    }
    const key = String(environment?.REACT_APP_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY || '').trim();
    if (!key || isUnsubstitutedEnvToken(key)) {
      return;
    }
    try {
      experimentClient = Experiment.initializeWithAmplitudeAnalytics(key, {
        automaticFetchOnAmplitudeIdentityChange: false,
        fetchOnStart: false,
      });
      emit({ type: 'init' });
    } catch (error) {
      experimentClient = null;
      emit({ type: 'init:error', error: error?.message || 'unknown' });
    }
  };

  const isExperimentClientReady = () =>
    experimentClient != null && !isExperimentOverrideDisabled();

  /**
   * Fetches assignments from Amplitude (sticky per user/device via SDK + Analytics identity).
   */
  const fetchAmplitudeExperimentVariants = async () => {
    if (!isAmplitudeExperimentsEnabled(window?.environment)) {
      return;
    }
    if (!experimentClient || isExperimentOverrideDisabled()) {
      return;
    }
    hasFetched = false;
    payloadCache.clear();
    variantCache.clear();
    emit({ type: 'fetch:start' });
    try {
      await experimentClient.fetch();
      hasFetched = true;
      emit({ type: 'fetch:success' });
    } catch (error) {
      /* UI uses fallbacks; hasFetched stays false so no $exposure fires for an unresolved assignment */
      emit({ type: 'fetch:error', error: error?.message || 'unknown' });
    }
  };

  /** Resets module singletons; used only from `amplitudeExperiment.test.js`. */
  const resetAmplitudeExperimentForTests = () => {
    experimentClient = null;
    experimentOverrideDisabled = false;
    hasFetched = false;
    payloadCache.clear();
    variantCache.clear();
    eventBuffer.length = 0;
    listeners.clear();
  };

  const readExperimentPayload = getExperimentPayload;

  /**
   * Read-only snapshot of internal experiment state. Intended for debug surfaces.
   * Reading the snapshot does NOT trigger `.variant()`, so it cannot fire `$exposure`.
   */
  const getExperimentSnapshot = () => {
    const experiments = Object.keys(EXPERIMENTS).map((experimentId) => {
      const config = EXPERIMENTS[experimentId];
      const cachedVariant = variantCache.get(experimentId);
      return {
        experimentId,
        flagKey: config.flagKey,
        fallback: config.fallback,
        fallbackVariant: getFallbackVariant(config),
        assignedVariant: cachedVariant?.key ?? null,
        assignedPayload: payloadCache.has(experimentId)
          ? payloadCache.get(experimentId)
          : null,
        exposed: variantCache.has(experimentId),
      };
    });
    return {
      hasFetched,
      isClientReady: experimentClient != null,
      isOverrideDisabled: experimentOverrideDisabled,
      experiments,
    };
  };

  return {
    getExperimentIds,
    getExperimentFallback,
    getExperimentPayload,
    getExperimentVariant,
    initializeAmplitudeExperiment,
    isExperimentClientReady,
    setExperimentOverrideDisabled,
    isExperimentOverrideDisabled,
    fetchAmplitudeExperimentVariants,
    resetAmplitudeExperimentForTests,
    readExperimentPayload,
    subscribeToExperimentEvents,
    getExperimentSnapshot,
    getExperimentEventHistory,
  };
};

const {
  getExperimentIds,
  getExperimentFallback,
  getExperimentPayload,
  getExperimentVariant,
  initializeAmplitudeExperiment,
  isExperimentClientReady,
  setExperimentOverrideDisabled,
  isExperimentOverrideDisabled,
  fetchAmplitudeExperimentVariants,
  resetAmplitudeExperimentForTests,
  readExperimentPayload,
  subscribeToExperimentEvents,
  getExperimentSnapshot,
  getExperimentEventHistory,
} = createAmplitudeExperiment();

/**
 * LOW-LEVEL — do not call these directly from components.
 * Reading variant/payload outside of the gate primitives bypasses the Redux
 * subscription and leaves the component stuck on the fallback value after the
 * experiment fetch completes. Use instead:
 *   - useExperiment(id)                              functional components
 *   - withExperiment(id, mapFn)(Component)           class components
 *   - <ExperimentGate experimentId={id} variants={}> JSX branching
 */
export {
  getExperimentIds,
  getExperimentFallback,
  getExperimentPayload,
  getExperimentVariant,
  initializeAmplitudeExperiment,
  isExperimentClientReady,
  setExperimentOverrideDisabled,
  isExperimentOverrideDisabled,
  fetchAmplitudeExperimentVariants,
  resetAmplitudeExperimentForTests,
  readExperimentPayload,
  subscribeToExperimentEvents,
  getExperimentSnapshot,
  getExperimentEventHistory,
};
